import { useEffect, useId, useRef, useState } from 'react';

import ModalActions from 'components/task-item/modal-actions';
import { TaskItemProps, useTaskItem } from 'components/task-item/useTaskItem';
import { PersonAvatar, initials } from 'components/person-avatar';
import { FOCO_VISIVEL } from 'utils/foco';

import { reduzirParticipacao, sairDaRealizacao } from 'services/participacao.service';
import { FaseDoCiclo } from '../realizacoes-do-semestre';
import { rostoDoAutor } from '../identidade-do-participante';
import AcoesDoParticipante from './AcoesDoParticipante';
import { etapasDaAgenda, HUMANOGRAMA, PilulaDaEtapa } from './AgendaDaRealizacao';
import DetalhesDaRealizacao from './DetalhesDaRealizacao';

/** Percentuais de atuação do cadastro, com o nome de cada faixa. */
export const ATUACOES = [
  { valor: 25, nome: 'Pontual' },
  { valor: 50, nome: 'Parcial' },
  { valor: 75, nome: 'Substancial' },
  { valor: 100, nome: 'Integral' },
];

export const FALHA_NA_ACAO = 'Não foi possível concluir. Tente novamente.';
export const AVISO_DE_REDUCAO = 'Seu percentual de atuação foi atualizado.';
export const AVISO_DE_SAIDA = 'Você saiu desta realização.';

type Painel = 'reduzir' | 'sair' | null;

/** O `team` pode trazer o percentual como texto ("50") ou sem ele; sem número não há o que mostrar nem reduzir. */
function percentualDaAtuacao(gravado: unknown): number | undefined {
  if (gravado === undefined || gravado === null || gravado === '') return undefined;
  const valor = Number(gravado);
  return Number.isFinite(valor) ? valor : undefined;
}

function rotuloDaAtuacao(percentual: number): string {
  const nome = ATUACOES.find(atuacao => atuacao.valor === percentual)?.nome;
  return nome ? `${percentual}% · ${nome}` : `${percentual}%`;
}

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] hover:underline disabled:cursor-not-allowed disabled:opacity-50 ${FOCO_VISIVEL}`;

export interface CartaoDeParticipacaoProps {
  data: any;
  /** Fase mensal em que a realização foi cadastrada. */
  fase: FaseDoCiclo | undefined;
  rotuloDaFase: string;
  playerInfo: any;
  /** Gravação confirmada (`status === 'OK'`): a página anuncia o aviso e relê o ciclo. */
  onConcluida: (aviso: string) => void;
  /** Depois de uma ação dos modais do "Ver mais" ou quando ele pede a leitura completa: a página relê o ciclo. */
  onRecarregar: () => void;
}

/**
 * Cartão de uma realização em que o usuário participa (RDN-12…RDN-17): Reduzir e Sair aparecem nas mesmas condições
 * do `TaskItem` (`canDecreaseParticipation`/`canExcludeParticipation` para a linha do próprio usuário) e gravam com o
 * mesmo payload dos modais de hoje.
 */
export default function CartaoDeParticipacao({
  data,
  fase,
  rotuloDaFase,
  playerInfo,
  onConcluida,
  onRecarregar,
}: CartaoDeParticipacaoProps) {
  const modelo = useTaskItem({ data, cycle: fase as TaskItemProps['cycle'], playerInfo, onReload: onRecarregar });
  const chave: string | undefined = modelo.user?._id;
  // O `TaskItem` só oferece as ações na linha do usuário entre os participantes (`target`).
  const eu = Array.isArray(data.target) ? data.target.find((pessoa: any) => pessoa._id === chave) : undefined;
  const percentual = chave ? percentualDaAtuacao(modelo.getUserTeam(chave)?.percent) : undefined;
  const podeReduzir = !!eu && modelo.canDecreaseParticipation(eu);
  const podeSair = !!eu && modelo.canExcludeParticipation(eu);
  const statusAtual = etapasDaAgenda(modelo).find(etapa => etapa.atual)?.status;

  const [painel, setPainel] = useState<Painel>(null);
  const [enviando, setEnviando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const tituloId = useId();
  const detalhesId = useId();
  const textoDoPainel = useRef<HTMLParagraphElement>(null);
  const botaoReduzir = useRef<HTMLButtonElement>(null);
  const botaoSair = useRef<HTMLButtonElement>(null);
  const painelAnterior = useRef<Painel>(null);

  const detalhesCarregados = !!data.description && !!data.team && !!data.target;

  // O botão que abre o painel sai da tela: o foco vai ao texto do painel e, ao cancelar, volta a quem o abriu.
  useEffect(() => {
    if (painel) {
      textoDoPainel.current?.focus();
    } else if (painelAnterior.current) {
      (painelAnterior.current === 'reduzir' ? botaoReduzir : botaoSair).current?.focus();
    }
    painelAnterior.current = painel;
  }, [painel]);

  function abrir(qual: Painel) {
    setFalhou(false);
    setPainel(qual);
  }

  // Fechar durante a gravação esconderia o desfecho dela; Cancelar e Voltar ficam `aria-disabled` como os demais (RDN-64).
  function fecharPainel() {
    if (enviando) return;
    abrir(null);
  }

  function alternarDetalhes() {
    const vaiAbrir = !modelo.oppened;
    if (vaiAbrir && (!data.team || !data.target)) modelo.handleReload();
    modelo.setOppened(vaiAbrir);
  }

  async function gravar(acao: () => Promise<boolean>, aviso: string) {
    if (enviando) return;
    setEnviando(true);
    setFalhou(false);
    const concluiu = await acao();
    setEnviando(false);

    if (concluiu) {
      // A página relê o ciclo e anuncia o aviso; não há botão de origem para onde devolver o foco.
      painelAnterior.current = null;
      setPainel(null);
      onConcluida(aviso);
    } else {
      setFalhou(true);
    }
  }

  return (
    <article
      aria-labelledby={tituloId}
      className={`flex flex-col gap-3 rounded-jj-lg border px-[18px] py-4 shadow-[0_1px_3px_rgba(17,24,39,.06)] ${
        painel ? 'border-brand-blue' : 'border-surface-300'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <PersonAvatar
          {...rostoDoAutor(data)}
          initials={initials(data.owner_name_guerra || data.title || '')}
          size={40}
          initialsClassName="bg-brand-green font-display text-xs font-bold text-brand-navy"
        />
        <div className="min-w-0 flex-1">
          <h3 id={tituloId} className="m-0 font-display text-[14.5px] font-bold leading-[1.3] text-brand-blue-dark">
            {data.title}
          </h3>
          <p className="m-0 mt-1 text-[11.5px] text-content-soft">
            Cadastrada por{' '}
            <a
              href={`${HUMANOGRAMA}${data.owner}`}
              target="_blank"
              rel="noreferrer"
              className={`font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
            >
              {`${data.owner_name_guerra ?? ''} (${data.owner})`}
            </a>
            {` · ${rotuloDaFase}`}
          </p>
        </div>
        <PilulaDaEtapa status={statusAtual} />
        <dl className="m-0 text-right">
          <dt className="text-[10px] text-content-soft">Pontos</dt>
          <dd className="m-0 font-display text-base font-bold text-brand-blue">{modelo.points}</dd>
        </dl>
      </div>

      {/* "Sair" segue só o `canExcludeParticipation`, como no `TaskItem`: não depende de haver percentual (RDN-15). */}
      {(typeof percentual === 'number' || podeSair) && (
        <div className="flex flex-wrap items-center gap-3 border-t border-surface-100 pt-3">
          {typeof percentual === 'number' && (
            <>
              <span className="text-[11px] text-content-soft">Sua atuação</span>
              <span className="rounded-jj-pill bg-brand-green px-[13px] py-[5px] font-display text-xs font-bold text-brand-navy">
                {rotuloDaAtuacao(percentual)}
              </span>
            </>
          )}
          {!painel && (podeReduzir || podeSair) && (
            <div className="ml-auto flex flex-wrap items-center gap-3.5">
              {podeReduzir && typeof percentual === 'number' && (
                <button
                  ref={botaoReduzir}
                  type="button"
                  onClick={() => abrir('reduzir')}
                  className={`${ACAO_DE_TEXTO} text-brand-blue`}
                >
                  Reduzir meu percentual
                </button>
              )}
              {podeSair && (
                // SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
                <button ref={botaoSair} type="button" onClick={() => abrir('sair')} className={`${ACAO_DE_TEXTO} text-urgent`}>
                  Sair da realização
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {painel === 'reduzir' && typeof percentual === 'number' && (
        <div className="flex flex-col gap-2.5 rounded-jj-md bg-surface-100 px-4 py-3.5">
          <p ref={textoDoPainel} tabIndex={-1} className="m-0 text-[11.5px] leading-[1.55] text-content outline-none">
            Escolha um percentual menor que o atual. Não é possível aumentar o seu percentual de atuação.
          </p>
          <div role="group" aria-label="Novo percentual de atuação" className="grid grid-cols-4 gap-1.5">
            {ATUACOES.map(atuacao => {
              const bloqueado = atuacao.valor >= percentual;
              const atual = atuacao.valor === percentual;
              return (
                <button
                  key={atuacao.valor}
                  type="button"
                  // Durante a gravação o botão fica `aria-disabled` para o foco não cair no `body`.
                  disabled={bloqueado}
                  aria-disabled={enviando || undefined}
                  aria-current={atual ? 'true' : undefined}
                  onClick={() => gravar(() => reduzirParticipacao(data._id, atuacao.valor, chave as string), AVISO_DE_REDUCAO)}
                  className={`flex flex-col items-center gap-0.5 rounded-jj-md border-2 px-1 py-2 disabled:cursor-not-allowed ${
                    atual ? 'border-brand-green bg-brand-white' : 'border-surface-200 bg-surface-100'
                  } ${bloqueado ? 'text-content-soft' : 'text-brand-blue-dark'} ${bloqueado && !atual ? 'opacity-40' : ''} ${FOCO_VISIVEL}`}
                >
                  <span className="font-display text-sm font-extrabold leading-none">{`${atuacao.valor}%`}</span>
                  <span className="text-[9px] leading-tight">{atuacao.nome}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            aria-disabled={enviando || undefined}
            onClick={fecharPainel}
            className={`${ACAO_DE_TEXTO} self-start text-content aria-disabled:opacity-50`}
          >
            Cancelar
          </button>
        </div>
      )}

      {painel === 'sair' && (
        <div className="flex flex-wrap items-center gap-3.5 rounded-jj-md bg-brand-pink-light px-4 py-3.5">
          <p
            ref={textoDoPainel}
            tabIndex={-1}
            className="m-0 flex-[1_1_260px] text-[11.5px] leading-[1.55] text-brand-navy outline-none"
          >
            <b>Tem certeza?</b> Ao sair, você deixa de receber os pontos e o Selo desta realização. Só quem cadastrou pode
            incluir você de novo.
          </p>
          <div className="flex gap-2.5">
            <button
              type="button"
              aria-disabled={enviando || undefined}
              onClick={fecharPainel}
              className={`rounded-jj-md border-0 bg-brand-white px-4 py-[9px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark aria-disabled:opacity-50 ${FOCO_VISIVEL}`}
            >
              Voltar
            </button>
            {/* SPEC_DEVIATION: fundo `urgent` no lugar do `brand-pink` do design (texto branco a 2,6:1 no rosa). */}
            <button
              type="button"
              aria-disabled={enviando || undefined}
              onClick={() => gravar(() => sairDaRealizacao(data._id, chave as string, modelo.ecoa), AVISO_DE_SAIDA)}
              className={`rounded-jj-md border-0 bg-urgent px-4 py-[9px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-white aria-disabled:opacity-50 ${FOCO_VISIVEL}`}
            >
              Sair da realização
            </button>
          </div>
        </div>
      )}

      <div id={detalhesId}>
        {modelo.oppened &&
          (detalhesCarregados ? (
            <DetalhesDaRealizacao
              data={data}
              getUserTeam={modelo.getUserTeam}
              getFormattedDate={modelo.getFormattedDate}
              getStepLabel={modelo.getStepLabel}
              ocultarPercentual={modelo.ecoa}
              // A linha do próprio usuário já tem Reduzir/Sair no cartão; as demais seguem o `TaskItem` (gestor do participante).
              acoesDoParticipante={pessoa =>
                pessoa._id !== chave && <AcoesDoParticipante pessoa={pessoa} modelo={modelo} />
              }
            />
          ) : (
            <p role="status" className="m-0 mt-4 text-center text-xs text-content-soft">
              Carregando informações…
            </p>
          ))}
      </div>

      <button
        type="button"
        aria-expanded={modelo.oppened}
        aria-controls={detalhesId}
        onClick={alternarDetalhes}
        className={`mx-auto flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-content-soft ${FOCO_VISIVEL}`}
      >
        <span aria-hidden="true" className="text-[13px] leading-none">
          {modelo.oppened ? '−' : '+'}
        </span>
        {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
      </button>

      {/* Sempre montada: o leitor de tela anuncia o aviso quando o texto entra (RDN-63). */}
      <p role="status" className="m-0 text-xs font-bold text-urgent">
        {falhou ? FALHA_NA_ACAO : ''}
      </p>

      <ModalActions
        callback={modelo.callBackActions}
        id={data._id}
        status={modelo.status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={modelo.validateTeamMember}
        activeEvaluationStep={modelo.activeEvaluationStep as number}
        bonus={modelo.bonus}
        userToChange={modelo.userToChange}
        ecoa={modelo.ecoa}
      />
    </article>
  );
}
